import { api } from '@@/services/api';
import { MESSAGES_PAGE_SIZE, Statuses } from '@@/constants/whatsapp';

export const whatsappApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChatsStatus: builder.query<{ status: Statuses; qr: string }, void>({
      query: () => `whatsapp/status`,
      providesTags: ['Chats'],
    }),
    getChats: builder.query<Unpatient.Chat[], null>({
      query: () => `whatsapp/chats`,
      providesTags: ['Chats'],
    }),
    getChatMessages: builder.query<
      Unpatient.ChatMessage[],
      { chatId: string; before?: string; limit?: number; forceSync?: boolean }
    >({
      query: ({ chatId, before, limit = MESSAGES_PAGE_SIZE, forceSync }) => ({
        url: `whatsapp/chats/${chatId}`,
        params: {
          limit,
          ...(before ? { before } : {}),
          ...(forceSync ? { forceSync: 1 } : {}),
        },
      }),
      providesTags: ['ChatMessages'],
      serializeQueryArgs: ({ queryArgs }) => queryArgs.chatId,
      merge: (existing, incoming, { arg }) => {
        if (!arg.before) {
          return incoming;
        }
        const known = new Set(existing.map((m) => m.id.id));
        const older = incoming.filter((m) => !known.has(m.id.id));
        return [...older, ...existing];
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.before !== previousArg?.before,
    }),
    getChatMessage: builder.query<
      Unpatient.ChatMessage,
      { chatId: string; messageId: string }
    >({
      query: ({ chatId, messageId }) => `whatsapp/chats/${chatId}/${messageId}`,
    }),
    getChatContact: builder.query<Unpatient.ChatContact, string>({
      query: (id) => `whatsapp/contacts/${id}`,
      providesTags: ['ChatContact'],
    }),
    getMessageMedia: builder.query<
      { data: string; mimetype: string; filename: string },
      { chatId: string; messageId: string }
    >({
      query: ({ chatId, messageId }) => ({
        url: `whatsapp/chats/${chatId}/${messageId}`,
        responseHandler: async (response) => {
          const { _media: media } = await response.json();
          return media;
        },
      }),
    }),
    sendMessage: builder.mutation<
      Unpatient.ChatMessage,
      { chatId: string; message: string; files?: FileList }
    >({
      query: ({ chatId, files, message }) => {
        const formData = new FormData();
        formData.append('message', message);
        if (files) {
          for (const file of Array.from(files)) {
            formData.append('file', file);
          }
        }
        return {
          url: `whatsapp/chats/${chatId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      // Invalidating ChatMessages triggers a refetch that surfaces the sent
      // message in the conversation immediately on API success. This relies
      // on `fetchChatMessages` in the API running gap-fill against wweb's
      // in-memory store when Mongo's lastMessageAt is stale — see
      // packages/api/src/controllers/WhatsappController.ts `fetchChatMessages`.
      // If that controller drops the gap-fill, the message will only appear
      // via the socket `msg` relay (~1s after wweb's async `message_create`).
      invalidatesTags: ['Chats', 'ChatMessages'],
    }),
    deleteMessage: builder.mutation<
      void,
      { chatId: string; messageId: string }
    >({
      query: ({ chatId, messageId }) => {
        return {
          url: `whatsapp/chats/${chatId}/${messageId}`,
          method: 'DELETE',
        };
      },
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(api.util.invalidateTags(['ChatMessages', 'Chats']));
        }, 1000);
      },
    }),
    editMessage: builder.mutation<
      void,
      { chatId: string; messageId: string; newText: string }
    >({
      query: ({ chatId, messageId, newText }) => {
        return {
          url: `whatsapp/chats/${chatId}/${messageId}`,
          method: 'PATCH',
          body: { newText },
        };
      },
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(api.util.invalidateTags(['ChatMessages', 'Chats']));
        }, 1000);
      },
    }),
    disconnect: builder.mutation<{}, void>({
      query: () => ({
        url: 'whatsapp/disconnect',
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Chats', 'ChatMessages', 'ChatContact'],
    }),
    getActivityMetrics: builder.query<Unpatient.ActivityMetrics, void>({
      query: () => `whatsapp/metrics`,
    }),
  }),
});

export const {
  useGetChatsQuery,
  useGetChatsStatusQuery,
  useGetChatMessagesQuery,
  useLazyGetChatMessagesQuery,
  useGetChatMessageQuery,
  useSendMessageMutation,
  useDisconnectMutation,
  useGetChatContactQuery,
  useGetMessageMediaQuery,
  useDeleteMessageMutation,
  useGetActivityMetricsQuery,
  useEditMessageMutation,
} = whatsappApi;
